import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Warehouse as WarehouseIcon, 
  Plus,
  Pencil,
  Trash,
  MapPin
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSellerMode } from '../contexts/SellerModeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WarehousesPage() {
  const { activeSeller } = useSellerMode();
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    is_active: true
  });

  // Re-fetch warehouses when seller mode changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      fetchWarehouses();
    }, 100);
    return () => clearTimeout(timer);
  }, [activeSeller?.user_id]);

  const fetchWarehouses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/warehouses`, { withCredentials: true });
      setWarehouses(response.data);
    } catch (error) {
      console.error('Failed to fetch warehouses:', error);
      toast.error('Failed to fetch warehouses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingWarehouse) {
        await axios.patch(`${API_URL}/api/warehouses/${editingWarehouse.warehouse_id}`, formData, { withCredentials: true });
        toast.success('Warehouse updated successfully');
      } else {
        await axios.post(`${API_URL}/api/warehouses`, formData, { withCredentials: true });
        toast.success('Warehouse created successfully');
      }
      
      fetchWarehouses();
      handleCloseDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save warehouse');
    }
  };

  const handleDelete = async (warehouseId) => {
    if (!window.confirm('Are you sure you want to delete this warehouse?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/warehouses/${warehouseId}`, { withCredentials: true });
      toast.success('Warehouse deleted successfully');
      fetchWarehouses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete warehouse');
    }
  };

  const handleToggleActive = async (warehouse) => {
    try {
      await axios.patch(`${API_URL}/api/warehouses/${warehouse.warehouse_id}`, 
        { is_active: !warehouse.is_active }, 
        { withCredentials: true }
      );
      toast.success(`Warehouse ${warehouse.is_active ? 'deactivated' : 'activated'}`);
      fetchWarehouses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update warehouse');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingWarehouse(null);
    setFormData({
      name: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      is_active: true
    });
  };

  const handleEdit = (warehouse) => {
    setEditingWarehouse(warehouse);
    setFormData({
      name: warehouse.name,
      address: warehouse.address,
      city: warehouse.city,
      state: warehouse.state,
      pincode: warehouse.pincode,
      is_active: warehouse.is_active
    });
    setDialogOpen(true);
  };

  return (
    <DashboardLayout 
      title="Warehouses" 
      subtitle="Manage your fulfillment locations"
      actions={
        <Button 
          onClick={() => setDialogOpen(true)}
          className="bg-[#7d64ff] hover:bg-[#6153a7] text-white"
          data-testid="add-warehouse-btn"
        >
          <Plus size={18} className="mr-2" />
          Add Warehouse
        </Button>
      }
    >
      {loading ? (
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      ) : warehouses.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {warehouses.map((warehouse) => (
            <div 
              key={warehouse.warehouse_id} 
              className={`bg-white border p-6 sp-card-hover ${warehouse.is_active ? 'border-[#e5e5ea]' : 'border-[#ea4b5a]/30 bg-[#ea4b5a]/5'}`}
              data-testid={`warehouse-card-${warehouse.warehouse_id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-[#7d64ff]/10 flex items-center justify-center rounded">
                  <WarehouseIcon size={24} className="text-[#7d64ff]" weight="bold" />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(warehouse)}
                    data-testid={`edit-warehouse-${warehouse.warehouse_id}`}
                  >
                    <Pencil size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[#ea4b5a] hover:text-[#c43447] hover:bg-[#ea4b5a]/10"
                    onClick={() => handleDelete(warehouse.warehouse_id)}
                    data-testid={`delete-warehouse-${warehouse.warehouse_id}`}
                  >
                    <Trash size={16} />
                  </Button>
                </div>
              </div>
              
              <h3 className="font-bold text-lg text-[#0A0A0A] mb-2">{warehouse.name}</h3>
              
              <div className="flex items-start gap-2 text-[#5a5c87] text-sm mb-4">
                <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                <div>
                  <p>{warehouse.address}</p>
                  <p>{warehouse.city}, {warehouse.state} - {warehouse.pincode}</p>
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-[#e5e5ea]">
                <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded ${warehouse.is_active ? 'sp-badge-success' : 'sp-badge-danger'}`}>
                  <span className={`status-dot ${warehouse.is_active ? 'success' : 'danger'}`}></span>
                  {warehouse.is_active ? 'Active' : 'Inactive'}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleToggleActive(warehouse)}
                  className={warehouse.is_active ? 'text-[#ea4b5a]' : 'text-[#28cd8c]'}
                  data-testid={`toggle-warehouse-${warehouse.warehouse_id}`}
                >
                  {warehouse.is_active ? 'Deactivate' : 'Activate'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-[#e5e5ea] p-12 text-center">
          <WarehouseIcon size={64} className="mx-auto text-[#e5e5ea] mb-4" />
          <h4 className="font-bold text-xl text-[#0A0A0A] mb-2">No warehouses yet</h4>
          <p className="text-[#5a5c87] mb-6">Add your first warehouse to start managing inventory locations</p>
          <Button 
            onClick={() => setDialogOpen(true)}
            className="bg-[#7d64ff] hover:bg-[#6153a7] text-white"
          >
            <Plus size={18} className="mr-2" />
            Add Warehouse
          </Button>
        </div>
      )}

      {/* Add/Edit Warehouse Dialog */}
      <Dialog open={dialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-bold">
              {editingWarehouse ? 'Edit Warehouse' : 'Add New Warehouse'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Warehouse Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g., Main Warehouse"
                className="mt-1 rounded-sm"
                data-testid="warehouse-name"
              />
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Address *</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
                placeholder="Street address"
                className="mt-1 rounded-sm"
                data-testid="warehouse-address"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">City *</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                  className="mt-1 rounded-sm"
                  data-testid="warehouse-city"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">State *</Label>
                <Input
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  required
                  className="mt-1 rounded-sm"
                  data-testid="warehouse-state"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Pincode *</Label>
                <Input
                  value={formData.pincode}
                  onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                  required
                  pattern="[0-9]{6}"
                  maxLength={6}
                  className="mt-1 rounded-sm"
                  data-testid="warehouse-pincode"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 rounded border-[#e5e5ea]"
                    data-testid="warehouse-active"
                  />
                  <span className="text-sm font-medium">Active</span>
                </label>
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <Button type="button" variant="outline" onClick={handleCloseDialog} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="save-warehouse-btn">
                {editingWarehouse ? 'Update Warehouse' : 'Add Warehouse'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
